/**
 * ProfileTab — Business identity, contact, address, and localization.
 *
 * This is the most regulation-sensitive tab: `legalName` and `email`
 * flow into E-Fatura (ADR-105) and notification channels, so validation
 * must be strict before save.
 *
 * The save flow is optimistic: on submit we show a saved-toast inline,
 * and roll back only if the mutation rejects. We do NOT close the form.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../shared/ui/Button';
import { FieldError } from '../../../shared/ui/FieldError';
import { Input } from '../../../shared/ui/Input';
import { Label } from '../../../shared/ui/Label';
import { Select } from '../../../shared/ui/Select';
import { Textarea } from '../../../shared/ui/Textarea';
import { cn } from '../../../shared/utils/cn';
import {
  useBusinessSettings,
  useUpdateBusinessSettings,
} from '../../../features/settings/useBusinessSettings';
import {
  profileSchema,
  type BusinessProfileSettings,
} from '../../../features/settings/types';
import { SettingsCard } from './SettingsCard';

const TIMEZONES = [
  'Europe/Istanbul',
  'Europe/London',
  'Europe/Berlin',
  'America/New_York',
  'America/Los_Angeles',
  'Asia/Dubai',
];

export function ProfileTab(): JSX.Element {
  const { t } = useTranslation('settings');
  const settingsQuery = useBusinessSettings();
  const updateMutation = useUpdateBusinessSettings();
  const [justSaved, setJustSaved] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<BusinessProfileSettings>({
    resolver: zodResolver(profileSchema),
    defaultValues: settingsQuery.data?.profile,
  });

  useEffect(() => {
    if (settingsQuery.data?.profile) {
      reset(settingsQuery.data.profile);
    }
  }, [settingsQuery.data?.profile, reset]);

  const onSubmit = async (values: BusinessProfileSettings) => {
    setJustSaved(false);
    await updateMutation.mutateAsync({ profile: values });
    reset(values);
    setJustSaved(true);
    window.setTimeout(() => setJustSaved(false), 2500);
  };

  if (settingsQuery.isLoading) {
    return <LoadingSkeleton />;
  }

  if (settingsQuery.isError) {
    return (
      <div
        role="alert"
        className="px-biz-content-lg py-biz-content rounded-biz-btn bg-biz-danger-subtle text-biz-danger border border-biz-danger/30 text-biz-sm"
      >
        {t('common.loadError')}
      </div>
    );
  }

  const fieldError = (key: keyof BusinessProfileSettings) => {
    const err = errors[key];
    if (!err) return null;
    if (err.message === 'Required' || err.message === '') {
      return t('common.required');
    }
    return err.message;
  };

  return (
    <SettingsCard
      title={t('profile.sectionTitle')}
      hint={t('profile.sectionHint')}
      onSubmit={handleSubmit(onSubmit)}
      footer={
        <>
          {justSaved ? (
            <span
              role="status"
              className="inline-flex items-center gap-1.5 text-biz-sm text-biz-success mr-auto"
            >
              <CheckCircle weight="regular" size={18} aria-hidden="true" />
              {t('common.saved')}
            </span>
          ) : null}
          <Button
            type="button"
            intent="secondary"
            onClick={() => settingsQuery.data && reset(settingsQuery.data.profile)}
            disabled={!isDirty || isSubmitting}
          >
            {t('common.cancel')}
          </Button>
          <Button type="submit" intent="primary" loading={isSubmitting} disabled={!isDirty}>
            {isSubmitting ? t('common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      <SectionLabel>{t('profile.sectionTitle')}</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-biz-content-lg">
        <Field id="legalName" label={t('profile.legalNameLabel')} hint={t('profile.legalNameHint')}>
          <Input id="legalName" invalid={Boolean(errors.legalName)} {...register('legalName')} />
          <FieldError id="legalName-error">{fieldError('legalName')}</FieldError>
        </Field>
        <Field
          id="displayName"
          label={t('profile.displayNameLabel')}
          hint={t('profile.displayNameHint')}
        >
          <Input
            id="displayName"
            invalid={Boolean(errors.displayName)}
            {...register('displayName')}
          />
          <FieldError id="displayName-error">{fieldError('displayName')}</FieldError>
        </Field>
      </div>

      <Field
        id="description"
        label={t('profile.descriptionLabel')}
        hint={t('profile.descriptionHint')}
      >
        <Textarea
          id="description"
          invalid={Boolean(errors.description)}
          {...register('description')}
        />
        <FieldError id="description-error">{fieldError('description')}</FieldError>
      </Field>

      <SectionLabel>{t('profile.contactSectionTitle')}</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-biz-content-lg">
        <Field id="email" label={t('profile.emailLabel')}>
          <Input
            id="email"
            type="email"
            inputMode="email"
            invalid={Boolean(errors.email)}
            {...register('email')}
          />
          <FieldError id="email-error">{fieldError('email')}</FieldError>
        </Field>
        <Field id="phone" label={t('profile.phoneLabel')}>
          <Input
            id="phone"
            type="tel"
            inputMode="tel"
            invalid={Boolean(errors.phone)}
            {...register('phone')}
          />
          <FieldError id="phone-error">{fieldError('phone')}</FieldError>
        </Field>
        <Field id="website" label={t('profile.websiteLabel')}>
          <Input
            id="website"
            type="url"
            inputMode="url"
            placeholder="https://"
            invalid={Boolean(errors.website)}
            {...register('website')}
          />
          <FieldError id="website-error">{fieldError('website')}</FieldError>
        </Field>
      </div>

      <SectionLabel>{t('profile.addressSectionTitle')}</SectionLabel>
      <Field id="addressLine" label={t('profile.addressLineLabel')}>
        <Input
          id="addressLine"
          invalid={Boolean(errors.addressLine)}
          {...register('addressLine')}
        />
      </Field>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-biz-content-lg">
        <Field id="city" label={t('profile.cityLabel')}>
          <Input id="city" invalid={Boolean(errors.city)} {...register('city')} />
        </Field>
        <Field id="postalCode" label={t('profile.postalCodeLabel')}>
          <Input
            id="postalCode"
            inputMode="numeric"
            invalid={Boolean(errors.postalCode)}
            {...register('postalCode')}
          />
        </Field>
      </div>

      <SectionLabel>{t('profile.localizationSectionTitle')}</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-biz-content-lg">
        <Field id="timezone" label={t('profile.timezoneLabel')}>
          <Select id="timezone" {...register('timezone')}>
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </Select>
        </Field>
        <Field id="currency" label={t('profile.currencyLabel')}>
          <Select id="currency" {...register('currency')}>
            <option value="TRY">TRY (₺)</option>
            <option value="EUR">EUR (€)</option>
            <option value="USD">USD ($)</option>
            <option value="GBP">GBP (£)</option>
          </Select>
        </Field>
        <Field id="locale" label={t('profile.localeLabel')}>
          <Select id="locale" {...register('locale')}>
            <option value="tr">Türkçe</option>
            <option value="en">English</option>
          </Select>
        </Field>
      </div>
    </SettingsCard>
  );
}

function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-biz-content">
      <Label htmlFor={id}>{label}</Label>
      {hint ? <span className="text-biz-xs text-biz-muted-foreground">{hint}</span> : null}
      {children}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <h3
      className={cn(
        'text-biz-xs font-semibold uppercase tracking-wider',
        'text-biz-muted-foreground',
        'pt-biz-content-lg first:pt-0',
      )}
    >
      {children}
    </h3>
  );
}

function LoadingSkeleton(): JSX.Element {
  return (
    <div className="space-y-biz-content-lg" aria-busy="true" aria-live="polite">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-16 rounded-biz-card bg-biz-muted animate-pulse"
          aria-hidden="true"
        />
      ))}
    </div>
  );
}
